import React, { useMemo } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { useLocation, useHistory, Link, NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import Select from 'react-select';
import DATE_FORMAT from '../../components/constants/dateFormat';
import utils from '../../utils/utils';
import Loader from '../../components/loader';
import AdminRouteOptimisationPlanEdit from './adminRoutePlanEdit';
import { useDeliveryDays, useRoutePlanCreation, useRoutePlan } from './apiHooks';
import AdminRouteOptimisationPlanCreate from './adminRoutePlanCreate';
import AdminRoutePlanRefresh from './adminRoutePlanRefresh';
import { parse, stringify } from 'qs';
import AdminRoutePlanBatchesWithoutDeliveries from './adminRoutePlanBatchesWithoutDeliveries';
import { Route } from 'react-router';
import ErrorMessage from '../../components/errorMessage';
import AdminRoutePlanDeliveryDayRoutes from './adminRoutePlanDeliveryDayRoutes';
import Helmet from 'react-helmet';

function useUrlParameterState(param) {
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const id = queryParams.get(param);

  const history = useHistory();
  const searchObj = parse(search, { ignoreQueryPrefix: true });

  const setNewValue = (newId, resetOtherQueryParams) => {
    searchObj[param] = newId;
    const newObj = resetOtherQueryParams ? { [param]: newId } : searchObj;
    history.replace({
      search: stringify(newObj, { ignoreQueryPrefix: false }),
    });
  };

  return [id, setNewValue];
}

function displayTimeRange(timeRange, deliveryDay) {
  return `${utils
    .getDateFromString(deliveryDay.deliveryDate)
    .format(DATE_FORMAT.displayOnlyDate)} ${utils.getFormattedTimeRange(timeRange)} ${timeRange.serviceAreaName}`;
}

const AdminRoutePlan = ({ match }) => {
  const [selectedTimeRangeIdFromUrl, setSelectedTimeRangeId] = useUrlParameterState('timeRangeId');
  const [selectedDeliveryDayIdFromUrl, setSelectedDeliveryDayId] = useUrlParameterState('deliveryDayId');
  const { isLoading: loadingDeliveryDays, deliveryDays } = useDeliveryDays({
    showAll: true,
    sortBy: 'deliveryDateDesc',
  });
  const { search } = useLocation();

  const selectedDeliveryDay =
    selectedDeliveryDayIdFromUrl && deliveryDays?.length
      ? deliveryDays.find((dd) => `${dd.id}` === selectedDeliveryDayIdFromUrl)
      : null;

  const selectedDeliveryDayOption = selectedDeliveryDay
    ? {
        value: selectedDeliveryDay.id,
        label: `${utils.getFormattedDateFromString(
          selectedDeliveryDay.deliveryDate,
          DATE_FORMAT.displayWeekdayAndDate
        )}`,
      }
    : null;

  const timeRanges = selectedDeliveryDay
    ? selectedDeliveryDay.timeRanges.map((timeRange) => ({ ...timeRange, deliveryDay: selectedDeliveryDay }))
    : [];

  const selectedTimeRange =
    selectedTimeRangeIdFromUrl && timeRanges?.length
      ? timeRanges.find((range) => `${range.id}` === selectedTimeRangeIdFromUrl)
      : null;

  const selectedTimeRangeOption = selectedTimeRange
    ? {
        value: selectedTimeRange.id,
        label: displayTimeRange(selectedTimeRange, selectedTimeRange.deliveryDay),
      }
    : null;

  const { routePlan } = useRoutePlan({
    deliveryDayTimeRangeId: selectedTimeRange?.id,
  });
  const { createRoutePlan, isLoading: routeLoading, isError: isRouteError, error: routeError } = useRoutePlanCreation();

  const { formatMessage } = useIntl();

  // TODO: handle errors centrally
  // TODO: add more required info to tables
  return (
    <div className="m-b-6">
      <Loader show={loadingDeliveryDays} />
      <Helmet>
        <title>{formatMessage({ id: 'admin.route.planning.header' })}</title>
      </Helmet>
      <h2 className="">
        <FormattedMessage id={'admin.route.planning.header'} />
      </h2>
      <div className="row">
        <div className="col-12 col-md-6">
          {deliveryDays ? (
            <div className="form-group">
              <label htmlFor="ddSelect">
                <FormattedMessage id={'admin.route.planning.selectors.delivery.day.label'} />
              </label>
              <Select
                name="ddSelect"
                value={selectedDeliveryDayOption}
                onChange={(range) => setSelectedDeliveryDayId(range.value, true)}
                options={deliveryDays.map((deliveryDay) => ({
                  value: deliveryDay.id,
                  label: `${utils.getFormattedDateFromString(
                    deliveryDay.deliveryDate,
                    DATE_FORMAT.displayWeekdayAndDate
                  )}`,
                }))}
              />
            </div>
          ) : (
            ''
          )}
        </div>
        <div className="col-12 col-md-6">
          <div className="form-group">
            <label htmlFor="trSelect">
              <FormattedMessage id={'admin.route.planning.selectors.time.range.label'} />
            </label>
            <Select
              name="trSelect"
              value={selectedTimeRangeOption}
              onChange={(range) => setSelectedTimeRangeId(range.value)}
              options={timeRanges.map((range) => ({
                value: range.id,
                label: displayTimeRange(range, range.deliveryDay),
              }))}
            />
          </div>
        </div>
      </div>

      <div className="m-b-3">
        <NavLink to={`${match.path}${search}`} exact className="link m-r-2">
          <FormattedMessage id={'admin.route.planning.buttons.all.delivery.day.routes'} />
        </NavLink>
        <NavLink to={`${match.path}/batchesWithoutDeliveries${search}`} className="link m-r-2">
          <FormattedMessage id={'admin.route.planning.buttons.batches.without.deliveries'} />
        </NavLink>
        <NavLink to={`${match.path}/timeRangeRoutes${search}`} className="link m-r-2">
          <FormattedMessage id={'admin.route.planning.buttons.routes'} />
        </NavLink>
      </div>

      {isRouteError ? <ErrorMessage error={utils.getErrorMessageFromResponse(routeError)} /> : ''}

      <Route
        path={`${match.path}/batchesWithoutDeliveries`}
        render={(props) =>
          selectedDeliveryDay ? (
            <AdminRoutePlanBatchesWithoutDeliveries selectedDeliveryDay={selectedDeliveryDay} {...props} />
          ) : (
            <div></div>
          )
        }
      />

      <Route
        path={`${match.path}/timeRangeRoutes`}
        render={(props) =>
          selectedTimeRange ? (
            <div className="m-t-4">
              {routePlan?.routeOptimisationResult?.status === 'SUCCEED' ? (
                <AdminRouteOptimisationPlanEdit
                  selectedDeliveryDay={selectedDeliveryDay}
                  routePlan={routePlan}
                  key={selectedTimeRange.id}
                />
              ) : routePlan ? (
                <AdminRoutePlanRefresh routePlan={routePlan} key={selectedTimeRange.id} />
              ) : (
                <AdminRouteOptimisationPlanCreate
                  key={selectedTimeRange.id}
                  timeRange={selectedTimeRange}
                  onCreateRoutePlan={createRoutePlan}
                  routeLoading={routeLoading}
                />
              )}
            </div>
          ) : (
            <div></div>
          )
        }
      />

      <Route
        exact
        path={`${match.path}`}
        render={(props) =>
          selectedDeliveryDay ? (
            <AdminRoutePlanDeliveryDayRoutes
              selectedDeliveryDay={selectedDeliveryDay}
              routePlan={routePlan}
              {...props}
            />
          ) : (
            ''
          )
        }
      />
    </div>
  );
};

AdminRoutePlan.propTypes = {};

AdminRoutePlan.defaultProps = {};

function mapStateToProps(state) {
  return {
    deliveryOrderBatches: state.deliveryOrderBatches.deliveryOrderBatches,
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(AdminRoutePlan));
